import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    MY_RFQ_DRAFTS_KEY,
    MY_RFQ_KEY,
    useRestartRFQ,
    useRFQDetails,
    useRFQMatchedListings,
    useUpdateRFQ,
    useWithdrawRFQ,
} from '@steelbuy/data-access';
import { NotFoundError } from '@steelbuy/error';
import { useModal } from '@steelbuy/modal-dialog';
import { RFQStatus } from '@steelbuy/ts-shared';
import { MarketingBannerSteelbuyPromise } from '@steelbuy/ui-domain';
import {
    ButtonCallToAction,
    ButtonPrimary,
    ButtonTertiaryOnLightM,
    Card,
    CardContentAttributes,
    CardContentAttributesItem,
    CardContentEditable,
    CardLayout,
    ConfirmCancelModel,
    IconIdentifier,
    LoadingSpinner,
    LoadingStatus,
    useSuccessErrorNotification,
} from '@steelbuy/ui-primitive';
import { isRequestQuotePath } from '@steelbuy/util';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { getSimilarRFQ } from '../create-rfq/createRFQUtil';
import { RFQDeadlineSelection } from '../create-rfq/step3/inputs/RFQDeadlineSelection';
import { RFQDeliverySelection } from '../create-rfq/step3/inputs/RFQDeliverySelection';
import { ListingsTable } from '../listings-table/ListingsTable';
import { PageHeader } from '../page-header/PageHeader';
import { CardContentRFQDetails } from '../rfqs/card-content-rfq-details/CardContentRFQDetails';
import { CardContentAdditionalComments } from '../rfqs/CardContentAdditionalComments';
import { CardContentDeliveryMonth } from '../rfqs/CardContentDeliveryMonth';
import { CardContentOrigins } from '../rfqs/CardContentOrigins';
import { CardContentQuoteDeadline } from '../rfqs/CardContentQuoteDeadline';
import { CardContentStatus } from '../rfqs/CardContentStatus.tsx';

import './RFQDetails.scss';
import { ListingsRejectionCard } from '../seller-listing-details/ListingsRejectionCard';

type RFQDetailsProps = {
    rfqId: string;
    goBackPath: string;
};

type RFQRenewalForm = {
    expiresAt: string;
    delivery: string;
};

const RADIX = 10;

export const RFQDetails = ({ goBackPath, rfqId }: RFQDetailsProps) => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const navigate = useNavigate();
    const modal = useModal();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { mutate, isLoading: isSubmitting } = useWithdrawRFQ();
    const isRequestQuote = isRequestQuotePath(location.pathname);
    const rfqGoBackPath = createRouteUrl(RoutePath.MY_REQUEST_QUOTE_DETAILS, ['rfqId', rfqId]);
    const {
        data: matchedListings,
        hasNextPage,
        fetchNextPage,
        isFetching,
        isFetchingNextPage,
        total,
        isLoading: isFindingMatchResults,
    } = useRFQMatchedListings(rfqId, {
        queryOptions: { enabled: isRequestQuote },
    });

    const { data, isLoading, error } = useRFQDetails({
        rfqId,
        queryOptions: {
            enabled: true,
        },
    });

    const {
        Notification: NotificationComponent,
        showSuccessNotification,
        showErrorNotification,
    } = useSuccessErrorNotification();

    const methods = useForm<RFQRenewalForm>({
        mode: 'onChange',
    });

    const { mutate: updateRFQ, isLoading: isUpdating } = useUpdateRFQ();
    const restartRFQ = useRestartRFQ();

    if (isLoading || isFindingMatchResults) {
        return <LoadingSpinner />;
    }
    if (error) {
        throw new NotFoundError();
    }

    const withdrawRFQ = () => {
        mutate(rfqId, {
            onSuccess: () => {
                queryClient.removeQueries({ queryKey: [MY_RFQ_KEY] });
                showSuccessNotification(t('translation:application.myRFQDetails.withdrawSuccess'));
                modal.hide();
            },
            onError: () => {
                showErrorNotification(t('translation:application.myRFQDetails.withdrawFailure'));
                modal.hide();
            },
        });
    };

    const isExpired = data?.status.value === RFQStatus.EXPIRED || false;
    const isRenewable = isExpired && !!data?.approved;
    const isWithdrawn = data?.status.value === RFQStatus.WITHDRAWN || false;
    const isRejected = data?.status.value === RFQStatus.REJECTED || false;

    const ButtonComponent = isExpired ? ButtonTertiaryOnLightM : ButtonCallToAction;

    const onSubmit = methods.handleSubmit((values) => {
        const [month, year] = values.delivery.split('_');
        const deliveryObject = {
            month: parseInt(month, RADIX),
            year: parseInt(year, RADIX),
        };
        const expiresAt = new Date(parseInt(values.expiresAt, 10)).toISOString();
        if (isExpired) {
            updateRFQ(
                {
                    rfqId,
                    payload: { expiresAt, delivery: deliveryObject },
                },
                {
                    onSuccess: ({ quoteReferenceNumber }) => {
                        queryClient.removeQueries({ queryKey: [MY_RFQ_KEY] });
                        navigate(RoutePath.MY_RFQS_DETAILS_SUCCESS, { state: { quoteReferenceNumber } });
                    },
                    onError: () => {
                        showErrorNotification(t('translation:application.myRFQDetails.updateFailure'));
                    },
                }
            );
        }
    });

    const editRejectedRFQ = () => {
        restartRFQ.mutate(
            { rfqId },
            {
                onSuccess: (draft) => {
                    navigate(`${RoutePath.CREATE_RFQ_WIZARD}/${draft.id}`, {
                        state: {
                            draftRFQ: draft,
                            rejectedRFQId: rfqId,
                        },
                    });
                    queryClient.removeQueries({ queryKey: [MY_RFQ_DRAFTS_KEY] });
                },
                onError: () => {
                    showErrorNotification(t('translation:application.myRFQDetails.restartRFQFailure'));
                },
            }
        );
    };

    return (
        <>
            <div>
                <PageHeader
                    pageTitle={data?.quoteReferenceNumber ?? ''}
                    previousPageTitle={t('application.myRFQ.header')}
                    onBackClick={() => navigate(goBackPath)}
                />
                {data && (
                    <form onSubmit={onSubmit}>
                        <div className="rfq-details">
                            {NotificationComponent}
                            {!isRequestQuote &&
                                (data.status.value === RFQStatus.UNDER_REVIEW ||
                                    data.status.value === RFQStatus.LIVE) && (
                                    <div className="rfq-details__buttons">
                                        <ButtonTertiaryOnLightM
                                            label={t('translation:application.myRFQDetails.withdrawRfq')}
                                            onClick={() => modal.show()}
                                        />
                                    </div>
                                )}

                            {isRequestQuote && matchedListings && matchedListings.length > 0 && (
                                <div className="match-listings">
                                    <Card isClickable={false}>
                                        <CardLayout>
                                            <CardContentAttributes
                                                header={t('application.myQuotes.matchListings.header')}
                                            >
                                                <CardContentAttributesItem
                                                    label={t('application.myQuotes.matchListings.subHeader')}
                                                    value=""
                                                >
                                                    <ListingsTable
                                                        listings={matchedListings}
                                                        formData={data.quoteEntry}
                                                        hasNextPage={hasNextPage}
                                                        loadMore={fetchNextPage}
                                                        isFetching={isFetching}
                                                        isFetchingNextPage={isFetchingNextPage}
                                                        total={total}
                                                        detailsPath={RoutePath.SEARCH_LISTING_DETAILS_FROM_RFQ}
                                                        goBackPath={rfqGoBackPath}
                                                    />
                                                </CardContentAttributesItem>
                                            </CardContentAttributes>
                                        </CardLayout>
                                    </Card>
                                </div>
                            )}
                            <CardContentStatus
                                status={data.status.value}
                                expiresAt={isExpired ? data.expiresAt.value : data.status.modifiedAt.value}
                                isRequestQuote={isRequestQuote}
                            />
                            {isRejected && (
                                <ListingsRejectionCard
                                    reason={data.status.reason ?? ''}
                                    internalSellerSku={data.quoteReferenceNumber}
                                    subject={t('application.myRFQ.emailSubject', {
                                        quoteRef: data.quoteReferenceNumber,
                                    })}
                                />
                            )}

                            <CardContentRFQDetails
                                quoteEntries={data.quoteEntry}
                                refNumber={data?.quoteReferenceNumber ?? ''}
                                status={data.status.value}
                                id={rfqId}
                            />
                            {data.origin && <CardContentOrigins origin={data.origin} />}

                            {!isRequestQuote &&
                                (isRenewable ? (
                                    <>
                                        <CardContentEditable>
                                            <RFQDeliverySelection {...methods} name="delivery" />
                                        </CardContentEditable>
                                        <CardContentEditable>
                                            <RFQDeadlineSelection {...methods} name="expiresAt" />
                                        </CardContentEditable>
                                    </>
                                ) : (
                                    <>
                                        <CardContentDeliveryMonth
                                            delivery={`${data.delivery.month}_${data.delivery.year}`}
                                        />
                                        <CardContentQuoteDeadline
                                            deadline={`${new Date(data.expiresAt.value).getTime()}`}
                                        />
                                    </>
                                ))}
                            {data.additionalInformation && (
                                <div className="rfq-details__comments">
                                    <CardContentAdditionalComments comments={data.additionalInformation} />
                                </div>
                            )}
                            <MarketingBannerSteelbuyPromise />

                            <div className="rfq-details__layout__actionbar">
                                {(isExpired || isWithdrawn) && (
                                    <ButtonComponent
                                        label={t('application.myRFQ.createSimilarRFQ')}
                                        onClick={() => {
                                            navigate(`${RoutePath.CREATE_RFQ_WIZARD}`, {
                                                state: {
                                                    createRFQData: getSimilarRFQ(data),
                                                },
                                            });
                                        }}
                                    />
                                )}
                                {isRejected && (
                                    <ButtonPrimary
                                        label={t('uiDomain:common.edit')}
                                        icon={IconIdentifier.EDIT}
                                        onClick={editRejectedRFQ}
                                        loadingStatus={
                                            restartRFQ.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE
                                        }
                                    />
                                )}
                                {isRenewable && (
                                    <ButtonCallToAction
                                        name="publish rfq button"
                                        label={t('application.myRFQ.publishRfq')}
                                        onClick={() => {}}
                                        type="submit"
                                        disabled={!methods.formState.isValid}
                                        loadingStatus={isUpdating ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                                    />
                                )}
                            </div>
                        </div>
                    </form>
                )}
            </div>
            <ConfirmCancelModel
                modalAccessor={modal}
                header={t('translation:application.myRFQDetails.withdrawModal.header')}
                cancelLabel={t('uiDomain:common.cancel')}
                submitLabel={t('translation:application.myRFQDetails.withdrawModal.confirmLabel')}
                onSubmit={withdrawRFQ}
                isSubmitting={isSubmitting}
                isDanger
                onClose={() => modal.hide()}
                blurOnClose
            />
        </>
    );
};
